import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatDate } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { Mail, RefreshCw, X, Trash2, Send, ArrowLeft, AlertTriangle, CheckCircle2, Copy, Link as LinkIcon } from "lucide-react";

const STATUS_TONE = {
  Sent: "bg-blue-50 text-blue-700 border-blue-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Expired: "bg-gray-100 text-gray-500 border-gray-200",
  Cancelled: "bg-gray-100 text-gray-500 border-gray-200",
};

export default function RiskAssessmentTab({ clientId, canEdit }) {
  const { user } = useAuth();
  const canManage = user && (user.role === "Admin" || user.role === "Advisor");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/clients/${clientId}/risk-assessments`); setRows(data); }
    catch { toast.error("Could not load assessments"); }
  };
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line

  const send = async () => {
    if (!window.confirm("Send a fresh Risk Assessment link to this client's email? Any existing pending link will be cancelled.")) return;
    setBusy(true);
    try { await api.post(`/clients/${clientId}/risk-assessments`); toast.success("Assessment link emailed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  const resend = async (aid) => { try { await api.post(`/clients/${clientId}/risk-assessments/${aid}/resend`); toast.success("Fresh link emailed"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); } };
  const cancel = async (aid) => { if (!window.confirm("Cancel this pending link?")) return; try { await api.post(`/clients/${clientId}/risk-assessments/${aid}/cancel`); toast.success("Cancelled"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); } };
  const del = async (aid) => { if (!window.confirm("Permanently delete this assessment record? This is audit-logged.")) return; try { await api.delete(`/clients/${clientId}/risk-assessments/${aid}`); toast.success("Deleted"); load(); setOpened(null); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); } };
  const open = async (aid) => { try { const { data } = await api.get(`/clients/${clientId}/risk-assessments/${aid}`); setOpened(data); } catch { toast.error("Could not open"); } };
  const applyDob = async (aid) => { try { await api.post(`/clients/${clientId}/risk-assessments/${aid}/apply-dob`); toast.success("DOB copied to client record"); open(aid); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  const latestCompleted = rows.find(r => r.status === "Completed");
  const monthsAgo = latestCompleted ? Math.round((Date.now() - new Date(latestCompleted.completed_at).getTime()) / (1000 * 60 * 60 * 24 * 30)) : null;

  if (opened) return <AssessmentDetail assessment={opened} onBack={() => { setOpened(null); load(); }} onDelete={() => del(opened.id)} onApplyDob={() => applyDob(opened.id)} canDelete={user?.role === "Admin"} />;

  return (
    <div className="space-y-6" data-testid="risk-tab">
      <Card className="flex flex-wrap items-center justify-between gap-4 border-border p-5">
        <div>
          <h3 className="font-dashboard text-base font-bold">Risk Profile Assessments</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {latestCompleted
              ? <>Last assessed <b>{monthsAgo === 0 ? "this month" : `${monthsAgo} month${monthsAgo === 1 ? "" : "s"} ago`}</b> — <b>{latestCompleted.risk_profile}</b> ({latestCompleted.assessment_score}/40)</>
              : "No completed assessment yet. Send the questionnaire to the client to begin."}
          </p>
        </div>
        {canManage && (
          <Button onClick={send} disabled={busy} className="gap-2" data-testid="send-assessment-btn">
            <Send className="h-4 w-4" /> {busy ? "Sending…" : "Send Risk Assessment"}
          </Button>
        )}
      </Card>

      <Card className="border-border p-5">
        <h4 className="mb-4 text-sm font-semibold">History</h4>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No assessments yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map(r => (
              <li key={r.id} className="flex flex-wrap items-start justify-between gap-3 py-3" data-testid={`row-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={"rounded-full border px-2 py-0.5 text-[10px] font-medium " + (STATUS_TONE[r.status] || "bg-gray-100 text-gray-700 border-gray-200")}>{r.status}</span>
                    {r.is_current && <Badge variant="secondary" className="text-[10px]">Current</Badge>}
                    {r.status === "Completed" && <span className="text-sm font-medium">{r.risk_profile} · {r.assessment_score}/40</span>}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Requested {formatDate(r.requested_at)}
                    {r.completed_at && <> · Completed {formatDate(r.completed_at)}</>}
                    {r.status === "Sent" && r.link_expires_at && <> · Expires {formatDate(r.link_expires_at)}</>}
                  </div>
                  {r.status === "Sent" && r.link_url && canManage && (
                    <ShareLinkRow url={r.link_url} expiresAt={r.link_expires_at} rowId={r.id} />
                  )}
                </div>
                <div className="flex gap-2">
                  {r.status === "Completed" && <Button size="sm" variant="outline" onClick={() => open(r.id)} data-testid={`open-${r.id}`}>Open</Button>}
                  {r.status === "Sent" && canManage && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => resend(r.id)} className="gap-1"><RefreshCw className="h-3 w-3" /> Resend</Button>
                      <Button size="sm" variant="outline" onClick={() => cancel(r.id)} className="gap-1"><X className="h-3 w-3" /> Cancel</Button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function ShareLinkRow({ url, expiresAt, rowId }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      // Fallback for older browsers / non-secure contexts.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Link copied — paste into WhatsApp");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — long-press the link to copy manually");
    }
  };
  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-muted/40 px-3 py-2"
      data-testid={`share-link-${rowId}`}
    >
      <LinkIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <code
        className="min-w-0 flex-1 truncate text-[11px] font-mono text-foreground/80"
        data-testid={`share-link-url-${rowId}`}
        title={url}
      >
        {url}
      </code>
      <Button
        size="sm"
        variant="outline"
        onClick={copy}
        className="h-7 gap-1 px-2 text-[11px]"
        data-testid={`copy-link-${rowId}`}
      >
        <Copy className="h-3 w-3" />
        {copied ? "Copied" : "Copy link"}
      </Button>
    </div>
  );
}

function AssessmentDetail({ assessment, onBack, onDelete, onApplyDob, canDelete }) {
  const a = assessment;
  return (
    <div className="space-y-6" data-testid="assessment-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">
        <ArrowLeft className="h-3 w-3" /> Back to history
      </button>

      {a.dob_mismatch && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="dob-mismatch">
          <AlertTriangle className="mr-1 inline h-4 w-4" />
          <b>DOB mismatch</b> — client record has <code>{a.dob_mismatch.stored}</code>, assessment captured <code>{a.dob_mismatch.submitted}</code>. Review before overwriting; the Life-Cycle Timeline uses the client's DOB.
        </div>
      )}
      {a.dob_offer_apply_to_client && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
          <span>The client record has no DOB. Apply <b>{a.dob}</b> to the client record?</span>
          <Button size="sm" onClick={onApplyDob} data-testid="apply-dob">Apply to client record</Button>
        </div>
      )}

      <Card className="border-primary/30 p-6" data-testid="profile-card">
        <p className="overline text-primary">Your Risk Profile</p>
        <h2 className="mt-1 font-dashboard text-2xl font-bold">{a.risk_profile} Investor</h2>
        <p className="mt-3 text-sm text-foreground/80">{a.profile_description}</p>
        <div className="mt-4 grid grid-cols-2 gap-4 border-t border-border pt-4 text-sm md:grid-cols-3">
          <div><p className="text-xs text-muted-foreground">Name</p><p className="font-medium">{a.client_name}</p></div>
          <div><p className="text-xs text-muted-foreground">Date of Birth</p><p className="font-medium">{a.dob}</p></div>
          <div><p className="text-xs text-muted-foreground">Age at assessment</p><p className="font-medium">{a.age_at_assessment} years</p></div>
          <div><p className="text-xs text-muted-foreground">Occupation</p><p className="font-medium">{a.occupation}</p></div>
          <div><p className="text-xs text-muted-foreground">Score</p><p className="font-medium">{a.assessment_score} / 40</p></div>
          <div><p className="text-xs text-muted-foreground">Completed</p><p className="font-medium">{formatDate(a.completed_at)}</p></div>
        </div>
      </Card>

      <Card className="border-border p-6">
        <h4 className="mb-4 text-sm font-semibold">Suggested Portfolio Allocation <span className="ml-2 text-[11px] font-normal text-muted-foreground">(illustrative guidance · advisor-only)</span></h4>
        <div className="grid grid-cols-3 gap-4">
          <Bucket label="Equity" value={a.suggested_allocation?.equity} tone="emerald" />
          <Bucket label="Debt" value={a.suggested_allocation?.debt} tone="blue" />
          <Bucket label="Gold" value={a.suggested_allocation?.gold} tone="amber" />
        </div>
        {a.suggested_allocation?.age_adjusted && (
          <div className="mt-3 inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs text-amber-800" data-testid="age-badge">
            Adjusted for age — equity capped at {a.suggested_allocation?.age_cap}%
          </div>
        )}
      </Card>

      <Card className="border-border p-6">
        <h4 className="mb-4 text-sm font-semibold">Equity Breakdown <span className="ml-2 text-[11px] font-normal text-muted-foreground">(illustrative)</span></h4>
        <div className="grid grid-cols-3 gap-4">
          <Bucket label="Large Cap" value={a.equity_breakdown?.large} tone="emerald" />
          <Bucket label="Mid Cap" value={a.equity_breakdown?.mid} tone="blue" />
          <Bucket label="Small Cap" value={a.equity_breakdown?.small} tone="amber" />
        </div>
      </Card>

      <Card className="border-border p-6">
        <h4 className="mb-1 text-sm font-semibold">Protection Check</h4>
        <p className="mb-4 text-[11px] text-muted-foreground">Self-reported by the client — a prompt for the Health/Term Insurance advice, not a substitute for reviewing actual policies.</p>
        <div className="grid gap-4 md:grid-cols-2">
          <ProtectionBox title="Health Insurance" p={a.protection_check?.health} />
          <ProtectionBox title="Term / Life Insurance" p={a.protection_check?.term} />
        </div>
      </Card>

      <Card className="border-border p-6">
        <h4 className="mb-4 text-sm font-semibold">Every question with the client's answer</h4>
        <ul className="space-y-3">
          {(a.responses || []).map((r, i) => (
            <li key={r.question_id} className="rounded-md border border-border p-3 text-sm">
              <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                <span>Q{i + 1}</span>
                <span className="uppercase tracking-wide">{r.question_id}</span>
                {!r.counts_in_score && <Badge variant="secondary" className="text-[10px]">Protection · not in score</Badge>}
                <span className="ml-auto font-mono">score: {r.score}</span>
              </div>
              <p className="font-medium">{r.question_text}</p>
              <p className="mt-1 text-foreground/80">→ {r.answer_text}</p>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="border-border p-5 text-xs text-muted-foreground">
        <div>Requested {formatDate(a.requested_at)} · Completed {formatDate(a.completed_at)} · Scoring version {a.scoring_version}</div>
        {a.link_email_sent_at && <div>Link email sent {formatDate(a.link_email_sent_at)}</div>}
        {a.result_email_sent_at && <div>Result email sent {formatDate(a.result_email_sent_at)}</div>}
      </Card>

      {canDelete && (
        <div className="flex justify-end">
          <Button variant="destructive" size="sm" onClick={onDelete} className="gap-1" data-testid="delete-btn"><Trash2 className="h-3 w-3" /> Delete assessment</Button>
        </div>
      )}
    </div>
  );
}

function Bucket({ label, value, tone }) {
  const tones = { emerald: "bg-emerald-50 text-emerald-700 border-emerald-200", blue: "bg-blue-50 text-blue-700 border-blue-200", amber: "bg-amber-50 text-amber-700 border-amber-200" };
  return (
    <div className={"rounded-lg border p-4 text-center " + (tones[tone] || "border-border")}>
      <p className="text-xs opacity-80">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value ?? "—"}%</p>
    </div>
  );
}

function ProtectionBox({ title, p }) {
  if (!p) return null;
  return (
    <div className="rounded-md border border-border p-4">
      <p className="text-xs text-muted-foreground">{title}</p>
      <p className="mt-1 font-semibold">{p.label} <span className="ml-1 text-[11px] text-muted-foreground">(score {p.score}/5)</span></p>
      <p className="mt-2 text-sm text-foreground/80">{p.advice}</p>
    </div>
  );
}
